import { FileIndicatorsView, Icon, StatusChip, documentStatusKey } from '@dataroom/shared';
import type {
  ClassificationStatus,
  FileIndicators,
  PaperworkKind,
  ProcessingStatus,
} from '@dataroom/shared';

/**
 * A document's FILING result as the ONE shared status chip (DataRoom UI
 * StatusChip — word + icon + colour, fixed labels), plus the two small
 * OCR / redaction indicators beside it (owner direction 2026-10-12: they run in
 * the background, per file):
 *   - `processing`       → "Classifying" (spinning icon)
 *   - `classified`       → "Filed" (+ "OCR pending / complete", "Redaction
 *                          pending / complete / needs review" when served)
 *   - `low_confidence`   → "Needs filing" (admin/seller; "Choose a subfolder"
 *                          when it already rests in a folder), or "Check AI" — a
 *                          person should look (opens the review)
 *   - `failed`           → "Couldn't file" — ADR-0018: the AI could not classify
 *                          the file even by its name (an unreadable file is
 *                          classified by name, so this is rare); file it by hand
 *   - `not_classifiable` → deal paperwork, never sent to the AI by design,
 *                          named by its kind: "Master NDA", "Buyer LOI",
 *                          "LOI counter" or "Agreement" (issued for signature)
 * Shared by the bulk-upload status list, the room's document table and the
 * review panel so all render identically.
 */
interface ClassificationStatusBadgeProps {
  status: ClassificationStatus | undefined;
  /** The document's processing step (the listing's `processingStatus`). */
  processingStatus?: ProcessingStatus | null;
  /** The OCR / redaction indicators (`fileIndicatorsOf(document)`); absent → none. */
  indicators?: FileIndicators;
  /** When set, the chip is a button that opens the classification review for
   * this document (the owner's "check the low confidence classifications" ask). */
  onClick?: () => void;
  /** What clicking does, e.g. "Review the classification of X" — appended to the
   * visible label for assistive tech and shown as the tooltip. */
  actionLabel?: string;
  /** Which deal paperwork a not-classifiable document is (the listing's `paperworkKind`). */
  paperworkKind?: PaperworkKind | null;
  /** An unpacked zip / saved email (the listing's `archive`): "Unpacked", not paperwork. */
  unpacked?: boolean;
  /** When set, a redaction that needs a person shows as a "Review redaction"
   * button that opens the redaction review of this document. */
  onReviewRedaction?: () => void;
  /** The document's name, for the review button's accessible name. */
  subject?: string;
  /** The document already rests in a folder (`categoryPath` not empty): "Choose a subfolder", not "Needs filing". */
  inFolder?: boolean;
}

export function ClassificationStatusBadge({
  status,
  onClick,
  actionLabel,
  paperworkKind,
  unpacked,
  processingStatus,
  indicators,
  onReviewRedaction,
  subject,
  inFolder,
}: ClassificationStatusBadgeProps) {
  const chip = (
    <StatusChip
      status={documentStatusKey(
        status,
        'categorized',
        paperworkKind,
        unpacked,
        processingStatus,
        inFolder,
      )}
      onClick={onClick}
      actionLabel={actionLabel}
    />
  );
  if (!indicators || (!indicators.ocr && !indicators.redaction)) return chip;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {chip}
      <FileIndicatorsView
        indicators={indicators}
        onReviewRedaction={onReviewRedaction}
        subject={subject}
      />
    </span>
  );
}

/** Small inline spinner. Decorative — the text beside it carries the meaning. */
export function Spinner() {
  return <Icon name="spinner" size={12} className="animate-spin" />;
}
